import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract, faPlus, faSignature, faPaperPlane, faCheckCircle, faBan, faTrash } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import ProviderShell, { useProviderApi } from './layouts/ProviderShell';

interface Contract {
    id: number;
    contract_number: string;
    title: string;
    contract_type: string;
    terms: string;
    status: 'draft' | 'sent' | 'signed' | 'active' | 'completed' | 'terminated';
    provider_signed: boolean;
    provider_signed_at: string | null;
    patient_signed: boolean;
    patient_signed_at: string | null;
    effective_date: string | null;
    expiry_date: string | null;
    created: string;
    patient: { patient_id: string; name: string };
}

interface PatientOption {
    patient_id: string;
    first_name: string;
    last_name: string;
}

const STATUS_COLORS: Record<string, string> = {
    draft: 'secondary', sent: 'info', signed: 'primary', active: 'success', completed: 'success', terminated: 'danger',
};

const TYPE_LABELS: Record<string, string> = {
    service_agreement: 'Service Agreement', consent_form: 'Consent Form', care_plan: 'Care Plan Agreement', custom: 'Custom',
};

const ProviderContracts: React.FC = () => {
    const { api } = useProviderApi();

    const [contracts, setContracts] = useState<Contract[]>([]);
    const [patients, setPatients] = useState<PatientOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');

    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ patient_id: '', title: '', contract_type: 'service_agreement', terms: '', effective_date: '', expiry_date: '' });
    const [submitting, setSubmitting] = useState(false);

    const [viewing, setViewing] = useState<Contract | null>(null);

    useEffect(() => { document.title = 'Contracts - Uzima Healthcare'; }, []);

    const loadAll = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            const [cRes, pRes] = await Promise.all([
                api.get(`practitioners/contracts/?${params}`),
                api.get('practitioners/patients/?status=active'),
            ]);
            if (cRes.data?.status === 'success') setContracts(cRes.data.data || []);
            if (pRes.data?.status === 'success') setPatients(pRes.data.data.map((p: any) => ({ patient_id: p.patient_id, first_name: p.first_name, last_name: p.last_name })));
        } catch { /* leave state as-is on failure */ } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const submitContract = async () => {
        if (!form.patient_id || !form.title.trim() || !form.terms.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Patient, title and terms are required.' });
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post('practitioners/contracts/', form);
            if (res.data?.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Contract Drafted', timer: 1500, showConfirmButton: false });
                setShowCreate(false);
                setForm({ patient_id: '', title: '', contract_type: 'service_agreement', terms: '', effective_date: '', expiry_date: '' });
                loadAll();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to create contract.' });
        } finally {
            setSubmitting(false);
        }
    };

    const doAction = async (contract: Contract, action: string) => {
        try {
            const res = await api.patch(`practitioners/contracts/${contract.id}/`, { action });
            if (res.data?.status === 'success') {
                setViewing(res.data.data);
                loadAll();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to update contract.' });
        }
    };

    const deleteContract = async (contract: Contract) => {
        const confirm = await Swal.fire({ title: 'Delete this contract?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc3545' });
        if (!confirm.isConfirmed) return;
        try {
            await api.delete(`practitioners/contracts/${contract.id}/`);
            setViewing(null);
            loadAll();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to delete contract.' });
        }
    };

    return (
        <ProviderShell>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faFileContract} className="me-2 text-primary" />Contracts</h2>
                    <p className="text-muted mb-0">Service agreements with your patients</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" />New Contract
                </button>
            </div>

            <div className="mb-3">
                <select className="form-select" style={{ maxWidth: 220 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="signed">Signed</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="terminated">Terminated</option>
                </select>
            </div>

            <div className="card">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : contracts.length === 0 ? (
                        <div className="text-center py-5">
                            <FontAwesomeIcon icon={faFileContract} className="text-muted mb-3" style={{ fontSize: '2.5rem' }} />
                            <h5>No contracts yet</h5>
                            <p className="text-muted">Draft a service agreement with a patient on your roster.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr><th>Contract #</th><th>Patient</th><th>Title</th><th>Type</th><th>Status</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {contracts.map(c => (
                                        <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setViewing(c)}>
                                            <td><span className="badge bg-light text-dark">{c.contract_number}</span></td>
                                            <td>{c.patient.name}</td>
                                            <td>{c.title}</td>
                                            <td>{TYPE_LABELS[c.contract_type] || c.contract_type}</td>
                                            <td><span className={`badge bg-${STATUS_COLORS[c.status]}`}>{c.status}</span></td>
                                            <td><button className="btn btn-sm btn-outline-primary" onClick={(e) => { e.stopPropagation(); setViewing(c); }}>View</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">New Contract</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Patient</label>
                                        <select className="form-select" value={form.patient_id} onChange={(e) => setForm(f => ({ ...f, patient_id: e.target.value }))}>
                                            <option value="">Select a patient...</option>
                                            {patients.map(p => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Type</label>
                                        <select className="form-select" value={form.contract_type} onChange={(e) => setForm(f => ({ ...f, contract_type: e.target.value }))}>
                                            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Title</label>
                                    <input type="text" className="form-control" placeholder="e.g. Ongoing Cardiology Care Agreement" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Terms</label>
                                    <textarea className="form-control" rows={6} value={form.terms} onChange={(e) => setForm(f => ({ ...f, terms: e.target.value }))} />
                                </div>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Effective Date</label>
                                        <input type="date" className="form-control" value={form.effective_date} onChange={(e) => setForm(f => ({ ...f, effective_date: e.target.value }))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Expiry Date</label>
                                        <input type="date" className="form-control" value={form.expiry_date} onChange={(e) => setForm(f => ({ ...f, expiry_date: e.target.value }))} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" disabled={submitting} onClick={submitContract}>{submitting ? 'Saving...' : 'Save Draft'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewing && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{viewing.title}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewing(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="d-flex gap-2 mb-3">
                                    <span className={`badge bg-${STATUS_COLORS[viewing.status]}`}>{viewing.status}</span>
                                    <span className="badge bg-light text-dark">{viewing.contract_number}</span>
                                    <span className="badge bg-light text-dark">{TYPE_LABELS[viewing.contract_type]}</span>
                                </div>
                                <p><strong>Patient:</strong> {viewing.patient.name}</p>
                                <p style={{ whiteSpace: 'pre-wrap' }}><strong>Terms:</strong><br />{viewing.terms}</p>
                                <div className="row g-3 mt-2">
                                    <div className="col-md-6">
                                        <small className="text-muted d-block">Provider Signature</small>
                                        <span className={viewing.provider_signed ? 'text-success' : 'text-muted'}>
                                            <FontAwesomeIcon icon={faSignature} className="me-1" />
                                            {viewing.provider_signed ? `Signed ${new Date(viewing.provider_signed_at!).toLocaleDateString()}` : 'Not signed'}
                                        </span>
                                    </div>
                                    <div className="col-md-6">
                                        <small className="text-muted d-block">Patient Signature</small>
                                        <span className={viewing.patient_signed ? 'text-success' : 'text-muted'}>
                                            <FontAwesomeIcon icon={faSignature} className="me-1" />
                                            {viewing.patient_signed ? `Signed ${new Date(viewing.patient_signed_at!).toLocaleDateString()}` : 'Not signed'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer flex-wrap">
                                {viewing.status === 'draft' && (
                                    <button className="btn btn-info" onClick={() => doAction(viewing, 'send')}><FontAwesomeIcon icon={faPaperPlane} className="me-1" />Send</button>
                                )}
                                {(viewing.status === 'draft' || viewing.status === 'sent') && !viewing.provider_signed && (
                                    <button className="btn btn-outline-primary" onClick={() => doAction(viewing, 'sign_provider')}><FontAwesomeIcon icon={faSignature} className="me-1" />Sign (Provider)</button>
                                )}
                                {(viewing.status === 'draft' || viewing.status === 'sent') && !viewing.patient_signed && (
                                    <button className="btn btn-outline-primary" onClick={() => doAction(viewing, 'sign_patient')}><FontAwesomeIcon icon={faSignature} className="me-1" />Sign (Patient)</button>
                                )}
                                {viewing.status === 'signed' && (
                                    <button className="btn btn-success" onClick={() => doAction(viewing, 'activate')}><FontAwesomeIcon icon={faCheckCircle} className="me-1" />Activate</button>
                                )}
                                {viewing.status === 'active' && (
                                    <button className="btn btn-success" onClick={() => doAction(viewing, 'complete')}><FontAwesomeIcon icon={faCheckCircle} className="me-1" />Mark Completed</button>
                                )}
                                {(viewing.status === 'active' || viewing.status === 'signed') && (
                                    <button className="btn btn-outline-danger" onClick={() => doAction(viewing, 'terminate')}><FontAwesomeIcon icon={faBan} className="me-1" />Terminate</button>
                                )}
                                {(viewing.status === 'draft' || viewing.status === 'sent') && (
                                    <button className="btn btn-outline-danger" onClick={() => deleteContract(viewing)}><FontAwesomeIcon icon={faTrash} className="me-1" />Delete</button>
                                )}
                                <button className="btn btn-light" onClick={() => setViewing(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </ProviderShell>
    );
};

export default ProviderContracts;
